import React, { useState, useCallback, useEffect } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
} from '@mellify/ui';
import {
  Globe,
  Map,
  Loader2,
  AlertCircle,
  CheckCircle2,
  Link as LinkIcon,
} from 'lucide-react';
import { useAddUrl } from '../hooks/useAddUrl';

interface AddURLModalProps {
  open: boolean;
  onClose: () => void;
}

type TabType = 'single' | 'sitemap';

const URL_REGEX = /^https?:\/\/.+\..+/;

export const AddURLModal: React.FC<AddURLModalProps> = ({ open, onClose }) => {
  const [activeTab, setActiveTab] = useState<TabType>('single');

  // Single URL state
  const [singleUrl, setSingleUrl] = useState('');
  const [singleTitle, setSingleTitle] = useState('');
  const [singleDescription, setSingleDescription] = useState('');

  // Sitemap state
  const [sitemapUrl, setSitemapUrl] = useState('');
  const [sitemapDepth, setSitemapDepth] = useState(2);
  const [respectRobotsTxt, setRespectRobotsTxt] = useState(true);

  const { crawlSingleUrl, crawlSitemap, isLoading, error, isSuccess, reset } = useAddUrl();

  const resetForm = useCallback(() => {
    setSingleUrl('');
    setSingleTitle('');
    setSingleDescription('');
    setSitemapUrl('');
    setSitemapDepth(2);
    setRespectRobotsTxt(true);
  }, []);

  const handleClose = useCallback(() => {
    if (isLoading) return;
    resetForm();
    reset();
    onClose();
  }, [isLoading, resetForm, reset, onClose]);

  // Auto-close modal after successful submission
  useEffect(() => {
    if (isSuccess) {
      const timer = setTimeout(() => {
        resetForm();
        reset();
        onClose();
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [isSuccess, resetForm, reset, onClose]);

  // Reset hook state when modal opens to ensure fresh state
  useEffect(() => {
    if (open) {
      reset();
      resetForm();
    }
  }, [open, reset, resetForm]);

  const handleSubmit = useCallback(async () => {
    if (activeTab === 'single') {
      await crawlSingleUrl({
        sourceUrl: singleUrl.trim(),
        title: singleTitle.trim() || undefined,
        description: singleDescription.trim() || undefined,
      });
    } else {
      await crawlSitemap({
        sourceUrl: sitemapUrl.trim(),
        maxDepth: sitemapDepth,
        respectRobotsTxt,
      });
    }
  }, [
    activeTab,
    singleUrl,
    singleTitle,
    singleDescription,
    sitemapUrl,
    sitemapDepth,
    respectRobotsTxt,
    crawlSingleUrl,
    crawlSitemap,
  ]);

  const handleDone = useCallback(() => {
    resetForm();
    reset();
    onClose();
  }, [resetForm, reset, onClose]);

  const isSingleUrlValid = URL_REGEX.test(singleUrl.trim());
  const isSitemapUrlValid = URL_REGEX.test(sitemapUrl.trim());
  const canSubmit =
    activeTab === 'single' ? isSingleUrlValid : isSitemapUrlValid;

  return (
    <Modal
      open={open}
      onClose={handleClose}
      size="lg"
      closeOnOverlayClick={!isLoading}
      closeOnEscape={!isLoading}
      aria-label="Add URL or Sitemap"
    >
      <ModalHeader>
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-info-50 dark:bg-info-900/20">
            <Globe className="w-5 h-5 text-info-600 dark:text-info-400" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-dark-900 dark:text-dark-100">
              Add URL
            </h2>
            <p className="text-xs text-dark-500 dark:text-dark-400 mt-0.5">
              Crawl a web page or sitemap to add to your knowledge base
            </p>
          </div>
        </div>
      </ModalHeader>

      <ModalBody className="space-y-5">
        {/* Success State */}
        {isSuccess && (
          <div className="flex flex-col items-center justify-center py-8 px-4">
            <div className="flex items-center justify-center w-14 h-14 rounded-2xl bg-success-50 dark:bg-success-900/20 mb-5">
              <CheckCircle2 className="w-7 h-7 text-success-500" />
            </div>
            <h3 className="text-lg font-semibold text-dark-900 dark:text-dark-100 mb-2">
              Crawl job queued!
            </h3>
            <p className="text-sm text-dark-500 dark:text-dark-400 text-center max-w-md">
              {activeTab === 'single'
                ? 'The URL will be crawled and the content will be added to your knowledge base shortly.'
                : 'The sitemap will be parsed and pages will be crawled according to your depth settings.'}
            </p>
          </div>
        )}

        {/* Error State */}
        {error && !isSuccess && (
          <div className="flex items-start gap-3 p-3 rounded-lg bg-danger-50 dark:bg-danger-900/10 border border-danger-200 dark:border-danger-800">
            <AlertCircle className="w-4 h-4 text-danger-500 shrink-0 mt-0.5" />
            <p className="text-sm text-danger-700 dark:text-danger-300">
              {error}
            </p>
          </div>
        )}

        {!isSuccess && (
          <>
            {/* Tabs */}
            <div className="flex border-b border-dark-200 dark:border-dark-700">
              <button
                type="button"
                onClick={() => setActiveTab('single')}
                className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'single'
                    ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                    : 'border-transparent text-dark-500 dark:text-dark-400 hover:text-dark-700 dark:hover:text-dark-200'
                }`}
                disabled={isLoading}
              >
                <LinkIcon className="w-4 h-4" />
                Single URL
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('sitemap')}
                className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'sitemap'
                    ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                    : 'border-transparent text-dark-500 dark:text-dark-400 hover:text-dark-700 dark:hover:text-dark-200'
                }`}
                disabled={isLoading}
              >
                <Map className="w-4 h-4" />
                Sitemap
              </button>
            </div>

            {/* Single URL Tab */}
            {activeTab === 'single' && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-dark-700 dark:text-dark-200 mb-1.5">
                    URL <span className="text-danger-500">*</span>
                  </label>
                  <input
                    type="url"
                    value={singleUrl}
                    onChange={(e) => setSingleUrl(e.target.value)}
                    placeholder="https://example.com/page"
                    disabled={isLoading}
                    className="block w-full px-3 py-2 text-sm rounded-md border border-dark-300 dark:border-dark-600 bg-white dark:bg-dark-800 text-dark-900 dark:text-dark-100 placeholder:text-dark-400 dark:placeholder:text-dark-500 focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                  {singleUrl && !isSingleUrlValid && (
                    <p className="mt-1.5 text-xs text-danger-500">
                      Please enter a valid URL starting with http:// or https://
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-dark-700 dark:text-dark-200 mb-1.5">
                    Title{' '}
                    <span className="text-dark-400 font-normal">(optional)</span>
                  </label>
                  <input
                    type="text"
                    value={singleTitle}
                    onChange={(e) => setSingleTitle(e.target.value)}
                    placeholder="Custom document title"
                    disabled={isLoading}
                    className="block w-full px-3 py-2 text-sm rounded-md border border-dark-300 dark:border-dark-600 bg-white dark:bg-dark-800 text-dark-900 dark:text-dark-100 placeholder:text-dark-400 dark:placeholder:text-dark-500 focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                  <p className="mt-1 text-xs text-dark-400 dark:text-dark-500">
                    Leave empty to auto-detect from the page title
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-dark-700 dark:text-dark-200 mb-1.5">
                    Description{' '}
                    <span className="text-dark-400 font-normal">(optional)</span>
                  </label>
                  <input
                    type="text"
                    value={singleDescription}
                    onChange={(e) => setSingleDescription(e.target.value)}
                    placeholder="Brief description of this page"
                    disabled={isLoading}
                    className="block w-full px-3 py-2 text-sm rounded-md border border-dark-300 dark:border-dark-600 bg-white dark:bg-dark-800 text-dark-900 dark:text-dark-100 placeholder:text-dark-400 dark:placeholder:text-dark-500 focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                </div>

                <div className="p-3 rounded-lg bg-dark-50 dark:bg-dark-800/50 border border-dark-200 dark:border-dark-700">
                  <p className="text-xs text-dark-500 dark:text-dark-400">
                    The page will be crawled and its main content extracted.
                    Navigation, footers, ads, and boilerplate elements are
                    automatically removed.
                  </p>
                </div>
              </div>
            )}

            {/* Sitemap Tab */}
            {activeTab === 'sitemap' && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-dark-700 dark:text-dark-200 mb-1.5">
                    Sitemap URL <span className="text-danger-500">*</span>
                  </label>
                  <input
                    type="url"
                    value={sitemapUrl}
                    onChange={(e) => setSitemapUrl(e.target.value)}
                    placeholder="https://example.com/sitemap.xml"
                    disabled={isLoading}
                    className="block w-full px-3 py-2 text-sm rounded-md border border-dark-300 dark:border-dark-600 bg-white dark:bg-dark-800 text-dark-900 dark:text-dark-100 placeholder:text-dark-400 dark:placeholder:text-dark-500 focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                  {sitemapUrl && !isSitemapUrlValid && (
                    <p className="mt-1.5 text-xs text-danger-500">
                      Please enter a valid URL starting with http:// or https://
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-dark-700 dark:text-dark-200 mb-1.5">
                    Crawl Depth
                  </label>
                  <div className="flex items-center gap-3">
                    <input
                      type="range"
                      min={1}
                      max={5}
                      value={sitemapDepth}
                      onChange={(e) => setSitemapDepth(parseInt(e.target.value, 10))}
                      disabled={isLoading}
                      className="flex-1 h-2 bg-dark-200 dark:bg-dark-700 rounded-lg appearance-none cursor-pointer accent-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    />
                    <span className="inline-flex items-center justify-center w-8 h-8 text-sm font-semibold text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/20 rounded-md">
                      {sitemapDepth}
                    </span>
                  </div>
                  <p className="mt-1 text-xs text-dark-400 dark:text-dark-500">
                    Maximum depth of nested sitemaps to follow (1 = only the
                    specified sitemap, 5 = deep crawl)
                  </p>
                </div>

                <div className="flex items-center justify-between p-3 rounded-lg border border-dark-200 dark:border-dark-700 bg-white dark:bg-dark-800">
                  <div>
                    <p className="text-sm font-medium text-dark-700 dark:text-dark-200">
                      Respect robots.txt
                    </p>
                    <p className="text-xs text-dark-400 dark:text-dark-500 mt-0.5">
                      Follow the site's robots.txt crawl directives
                    </p>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={respectRobotsTxt}
                    onClick={() => setRespectRobotsTxt((prev) => !prev)}
                    disabled={isLoading}
                    className={`
                      relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out
                      focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:ring-offset-2
                      disabled:opacity-50 disabled:cursor-not-allowed
                      ${respectRobotsTxt ? 'bg-primary-500' : 'bg-dark-300 dark:bg-dark-600'}
                    `}
                  >
                    <span
                      className={`
                        pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow-lg ring-0 transition duration-200 ease-in-out
                        ${respectRobotsTxt ? 'translate-x-5' : 'translate-x-0'}
                      `}
                    />
                  </button>
                </div>

                <div className="p-3 rounded-lg bg-dark-50 dark:bg-dark-800/50 border border-dark-200 dark:border-dark-700">
                  <p className="text-xs text-dark-500 dark:text-dark-400">
                    The sitemap will be parsed and each page URL will be crawled
                    individually. Main content will be extracted and boilerplate
                    removed automatically.
                  </p>
                </div>
              </div>
            )}
          </>
        )}
      </ModalBody>

      <ModalFooter>
        {isSuccess ? (
          <Button variant="primary" size="md" onClick={handleDone}>
            Done
          </Button>
        ) : (
          <>
            <Button
              variant="outline"
              size="md"
              onClick={handleClose}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              size="md"
              leftIcon={
                isLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : activeTab === 'single' ? (
                  <Globe className="w-4 h-4" />
                ) : (
                  <Map className="w-4 h-4" />
                )
              }
              onClick={handleSubmit}
              disabled={isLoading || !canSubmit}
              loading={isLoading}
            >
              {isLoading
                ? 'Submitting...'
                : activeTab === 'single'
                  ? 'Crawl URL'
                  : 'Crawl Sitemap'}
            </Button>
          </>
        )}
      </ModalFooter>
    </Modal>
  );
};

AddURLModal.displayName = 'AddURLModal';